import { useCallback } from "react";
import { appStore } from "../app/store";
import { useShallow } from "zustand/shallow";
import type { Agent, AgentTemplate } from "app-types/agent";
import type { SequentialStep } from "../app/store";

/**
 * Hook for accessing agent management state and actions
 */
export function useAgentManagement() {
  return appStore(
    useShallow((state) => ({
      // State
      availableAgents: state.agents.availableAgents,
      availableTemplates: state.agents.availableTemplates,
      selectedAgent: state.agents.selectedAgent,
      isLoadingAgents: state.agents.isLoadingAgents,
      agentError: state.agents.agentError,

      // Actions
      setAvailableAgents: state.setAvailableAgents,
      setAvailableTemplates: state.setAvailableTemplates,
      selectAgent: state.selectAgent,
      setAgentLoading: state.setAgentLoading,
      setAgentError: state.setAgentError,
    })),
  );
}

/**
 * Hook for accessing agent thread management
 */
export function useAgentThreads() {
  return appStore(
    useShallow((state) => ({
      // State
      agentThreads: state.agents.agentThreads,

      // Actions
      setAgentThread: state.setAgentThread,
      removeAgentThread: state.removeAgentThread,
    })),
  );
}

/**
 * Hook for accessing sequential processing state and actions
 */
export function useSequentialProcessing() {
  return appStore(
    useShallow((state) => ({
      // State
      sequentialProcessing: state.agents.sequentialProcessing,

      // Actions
      startSequentialProcessing: state.startSequentialProcessing,
      updateSequentialStep: state.updateSequentialStep,
      setCurrentSequentialStep: state.setCurrentSequentialStep,
      completeSequentialProcessing: state.completeSequentialProcessing,
      failSequentialProcessing: state.failSequentialProcessing,
      resetSequentialProcessing: state.resetSequentialProcessing,
    })),
  );
}

/**
 * Hook for getting the current agent's thread ID
 */
export function useCurrentAgentThread() {
  return appStore(
    useShallow((state) => {
      const selectedAgent = state.agents.selectedAgent;
      if (!selectedAgent) return null;

      return state.agents.agentThreads[selectedAgent.id] || null;
    }),
  );
}

/**
 * Hook for checking if an agent is currently selected
 */
export function useIsAgentSelected() {
  return appStore(
    useShallow((state) => ({
      isAgentSelected: state.agents.selectedAgent !== null,
      selectedAgentId: state.agents.selectedAgent?.id || null,
      selectedAgentName: state.agents.selectedAgent?.name || null,
    })),
  );
}

/**
 * Hook for managing agent selection with automatic thread handling
 */
export function useAgentSelection() {
  const { selectAgent, setAgentThread } = appStore(
    useShallow((state) => ({
      selectAgent: state.selectAgent,
      setAgentThread: state.setAgentThread,
    })),
  );

  const selectAgentWithThread = useCallback(
    (agent: Agent | null, threadId?: string) => {
      selectAgent(agent);

      // If selecting an agent and a thread ID is provided, associate them
      if (agent && threadId) {
        setAgentThread(agent.id, threadId);
      }
    },
    [selectAgent, setAgentThread],
  );

  return {
    selectAgentWithThread,
    selectAgent,
  };
}

/**
 * Hook for sequential processing progress tracking
 */
export function useSequentialProgress() {
  return appStore(
    useShallow((state) => {
      const processing = state.agents.sequentialProcessing;

      return {
        isActive: processing.isActive,
        progress:
          processing.totalSteps > 0
            ? (processing.currentStepIndex / processing.totalSteps) * 100
            : 0,
        currentStep: processing.steps[processing.currentStepIndex] || null,
        completedSteps: processing.steps.filter(
          (step) => step.status === "completed",
        ).length,
        failedSteps: processing.steps.filter((step) => step.status === "failed")
          .length,
        totalSteps: processing.totalSteps,
        isCompleted: processing.isCompleted,
        error: processing.error,
        startedAt: processing.startedAt,
        completedAt: processing.completedAt,
      };
    }),
  );
}

/**
 * Hook for getting agent-specific utilities
 */
export function useAgentUtils() {
  const { availableAgents, selectedAgent } = useAgentManagement();
  const { agentThreads } = useAgentThreads();

  const getAgentById = useCallback(
    (agentId: string): Agent | undefined => {
      return availableAgents.find((agent) => agent.id === agentId);
    },
    [availableAgents],
  );

  const getAgentThreadId = useCallback(
    (agentId: string): string | undefined => {
      return agentThreads[agentId];
    },
    [agentThreads],
  );

  const isAgentActive = useCallback(
    (agentId: string): boolean => {
      return selectedAgent?.id === agentId;
    },
    [selectedAgent],
  );

  return {
    getAgentById,
    getAgentThreadId,
    isAgentActive,
  };
}
